import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Search, ChevronDown, ChevronRight, Star, X, ArrowLeft, Sun } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Lumisial } from '../lib/supabase';
import { lumisialService } from '../services/lumisialService';
import { useLumisial } from '../context/LumisialContext';
import { normalizeText } from '../utils/textNormalization';
import { TopBar } from './TopBar';

interface LumisialGrouped {
  [provinciaId: string]: {
    provinciaNombre: string;
    diocesis: {
      [diocesisId: string]: {
        diocesisNombre: string;
        diocesisTipo: 'diocesis' | 'prefectura';
        lumisiales: Lumisial[];
      };
    };
  };
}

const LAST_LUMISIAL_KEY = 'last_lumisial';
const LAST_PROVINCIA_EXPANDED_KEY = 'last_provincia_expanded';

// Escapa caracteres especiales de regex para prevenir ReDoS
const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
};

function highlightMatch(text: string, search: string): string | React.ReactElement {
  if (!search.trim()) return text;
  const parts = text.split(new RegExp(`(${escapeRegExp(search)})`, 'gi'));
  let charPos = 0;
  return (
    <span>
      {parts.map(part => {
        const key = charPos;
        charPos += part.length;
        return part.toLowerCase() === search.toLowerCase() ? (
          <mark key={key} className="bg-yellow-200 text-slate-900 font-medium">{part}</mark>
        ) : (
          <span key={key}>{part}</span>
        );
      })}
    </span>
  );
}

interface LumisialButtonProps {
  readonly lumisial: Lumisial;
  readonly searchTerm: string;
  readonly onSelect: (lumisial: Lumisial) => void;
  /** El lumisial que ya está activo: se marca en vez de dejarlo indistinguible del
   * resto. Tocarlo no necesita un manejador propio — `onSelect` con el mismo
   * lumisial produce exactamente "volver a él" (spec, decisión de diseño 12). */
  readonly isActive: boolean;
}

function LumisialButton({ lumisial, searchTerm, onSelect, isActive }: LumisialButtonProps) {
  return (
    <button
      type="button"
      onClick={() => onSelect(lumisial)}
      className={`w-full text-left p-3 rounded-lg transition-colors border mb-2 ${
        isActive
          ? 'border-blue-300 bg-blue-50 hover:bg-blue-100'
          : 'border-transparent hover:bg-slate-50 hover:border-slate-200'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <h4 className="font-semibold text-slate-800">
          {highlightMatch(lumisial.nombre, searchTerm)}
        </h4>
        {isActive && (
          <span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
            Actual
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600">
        {highlightMatch(lumisial.ciudad, searchTerm)}
        {lumisial.region && (
          <>, {highlightMatch(lumisial.region, searchTerm)}</>
        )}
      </p>
    </button>
  );
}

export const LumisialSelector: React.FC = () => {
  const [lumisiales, setLumisiales] = useState<Lumisial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProvincias, setExpandedProvincias] = useState<Set<string>>(new Set());
  const [expandedDiocesis, setExpandedDiocesis] = useState<Set<string>>(new Set());
  const { selectedLumisial, setSelectedLumisial } = useLumisial();
  const navigate = useNavigate();

  // Con un lumisial activo, el título dice "Cambiar" (hay algo de qué salir) y el
  // encabezado mantiene ese contexto en vez de saltar al título genérico de la app —
  // le da continuidad visual a quien entró desde el indicador o el menú del avatar.
  // En sesión nueva (sin lumisial activo todavía) no hay nada que "cambiar": el título
  // sigue siendo "Seleccionar" y el encabezado usa el de la app, como antes.
  const tituloPantalla = selectedLumisial ? 'Cambiar Lumisial' : 'Seleccionar Lumisial';
  const contextoEncabezado = selectedLumisial
    ? { etiqueta: `L. ${selectedLumisial.nombre}`, icon: <Sun className="w-4 h-4" /> }
    : null;

  const loadLumisiales = useCallback(async () => {
    try {
      setLoading(true);
      const data = await lumisialService.getAll();
      setLumisiales(data);
    } catch (err) {
      setError('Error al cargar los lumisiales');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPreferences = useCallback(() => {
    const lastProvinciaExpanded = localStorage.getItem(LAST_PROVINCIA_EXPANDED_KEY);
    if (lastProvinciaExpanded) {
      setExpandedProvincias(new Set([lastProvinciaExpanded]));
    }
  }, []);

  useEffect(() => {
    loadLumisiales();
    loadPreferences();
  }, [loadLumisiales, loadPreferences]);

  const getLastLumisial = (): Lumisial | null => {
    try {
      const lastLumisialStr = localStorage.getItem(LAST_LUMISIAL_KEY);
      if (lastLumisialStr) {
        const lastLumisialData = JSON.parse(lastLumisialStr);
        const lumisial = lumisiales.find(l => l.id === lastLumisialData.id);
        return lumisial || null;
      }
    } catch (err) {
      console.error('Error loading last lumisial:', err);
    }
    return null;
  };

  const handleSelectLumisial = (lumisial: Lumisial) => {
    localStorage.setItem(LAST_LUMISIAL_KEY, JSON.stringify({
      id: lumisial.id,
      timestamp: Date.now()
    }));
    if (lumisial.provincia_eclesiastica_id) {
      localStorage.setItem(LAST_PROVINCIA_EXPANDED_KEY, lumisial.provincia_eclesiastica_id);
    }
    setSelectedLumisial(lumisial);
    // Con la ruta propia de esta pantalla (`/lumisial/cambiar`), elegir un lumisial ya
    // no basta para salir de aquí: la URL sigue matcheando "cambiar" hasta que se
    // navega explícitamente. Antes no hacía falta — esta pantalla no tenía ruta y el
    // cambio de estado ya alcanzaba para que el contenedor mostrara otra cosa.
    navigate('/lumisial/home');
  };

  const toggleProvincia = (provinciaId: string) => {
    setExpandedProvincias(prev => {
      const newSet = new Set(prev);
      if (newSet.has(provinciaId)) {
        newSet.delete(provinciaId);
      } else {
        newSet.add(provinciaId);
      }
      return newSet;
    });
  };

  const toggleDiocesis = (diocesisId: string) => {
    setExpandedDiocesis(prev => {
      const newSet = new Set(prev);
      if (newSet.has(diocesisId)) {
        newSet.delete(diocesisId);
      } else {
        newSet.add(diocesisId);
      }
      return newSet;
    });
  };

  const lumisialesGrouped = useMemo((): LumisialGrouped => {
    const grouped: LumisialGrouped = {};

    lumisiales.forEach(lumisial => {
      const provinciaId = lumisial.provincia_eclesiastica_id;
      const diocesisId = lumisial.diocesis_id;
      const provinciaNombre = lumisial.provincia_eclesiastica?.nombre || 'Sin Provincia';
      const diocesisNombre = lumisial.diocesis?.nombre || 'Sin Diócesis';
      const diocesisTipo = lumisial.diocesis?.tipo || 'diocesis';

      if (!grouped[provinciaId]) {
        grouped[provinciaId] = {
          provinciaNombre,
          diocesis: {}
        };
      }

      if (!grouped[provinciaId].diocesis[diocesisId]) {
        grouped[provinciaId].diocesis[diocesisId] = {
          diocesisNombre,
          diocesisTipo,
          lumisiales: []
        };
      }

      grouped[provinciaId].diocesis[diocesisId].lumisiales.push(lumisial);
    });

    return grouped;
  }, [lumisiales]);

  const filteredLumisiales = useMemo(() => {
    if (!searchTerm.trim()) return lumisiales;

    const searchNormalized = normalizeText(searchTerm);
    return lumisiales.filter(lumisial =>
      normalizeText(lumisial.nombre).includes(searchNormalized) ||
      normalizeText(lumisial.ciudad).includes(searchNormalized) ||
      (lumisial.region && normalizeText(lumisial.region).includes(searchNormalized)) ||
      (lumisial.diocesis?.nombre && normalizeText(lumisial.diocesis.nombre).includes(searchNormalized)) ||
      (lumisial.provincia_eclesiastica?.nombre && normalizeText(lumisial.provincia_eclesiastica.nombre).includes(searchNormalized))
    );
  }, [lumisiales, searchTerm]);

  const expandAllInGrouped = useCallback((grouped: LumisialGrouped) => {
    const provincias = Object.keys(grouped);
    const diocesisList = provincias.flatMap(p => Object.keys(grouped[p].diocesis));
    setExpandedProvincias(prev => new Set([...prev, ...provincias]));
    setExpandedDiocesis(prev => new Set([...prev, ...diocesisList]));
  }, []);

  const filteredGrouped = useMemo((): LumisialGrouped => {
    if (!searchTerm.trim()) return lumisialesGrouped;

    const grouped: LumisialGrouped = {};

    filteredLumisiales.forEach(lumisial => {
      const provinciaId = lumisial.provincia_eclesiastica_id;
      const diocesisId = lumisial.diocesis_id;
      const provinciaNombre = lumisial.provincia_eclesiastica?.nombre || 'Sin Provincia';
      const diocesisNombre = lumisial.diocesis?.nombre || 'Sin Diócesis';
      const diocesisTipo = lumisial.diocesis?.tipo || 'diocesis';

      if (!grouped[provinciaId]) {
        grouped[provinciaId] = { provinciaNombre, diocesis: {} };
      }

      if (!grouped[provinciaId].diocesis[diocesisId]) {
        grouped[provinciaId].diocesis[diocesisId] = { diocesisNombre, diocesisTipo, lumisiales: [] };
      }

      grouped[provinciaId].diocesis[diocesisId].lumisiales.push(lumisial);
    });

    expandAllInGrouped(grouped);

    return grouped;
  }, [filteredLumisiales, lumisialesGrouped, searchTerm, expandAllInGrouped]);

  const lastLumisial = getLastLumisial();
  const totalCount = Object.keys(filteredGrouped).length;

  // Esta pantalla solo es alcanzable con acceso a varios lumisiales (T2 la gatea a
  // nivel de ruta), así que siempre hay una salida — nunca es un callejón. Nombra su
  // destino en vez del "Volver" anterior, que además navegaba a una dirección
  // inexistente (spec, decisión de diseño 12).
  const handleCancelarOIrAAdmin = () => {
    if (selectedLumisial) {
      navigate('/lumisial/home');
    } else {
      navigate('/admin');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {contextoEncabezado ? (
          <TopBar contexto={contextoEncabezado} />
        ) : (
          <TopBar isMainHome mainTitle="Gestión Administrativa Gnóstica" />
        )}
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">{tituloPantalla}</h2>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-slate-700 mx-auto"></div>
            <p className="mt-4 text-slate-600 font-medium">Cargando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {contextoEncabezado ? (
          <TopBar contexto={contextoEncabezado} />
        ) : (
          <TopBar isMainHome mainTitle="Gestión Administrativa Gnóstica" />
        )}
        <div className="flex flex-col items-center justify-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">{tituloPantalla}</h2>
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={loadLumisiales}
              className="mt-4 w-full bg-slate-700 text-white py-2 px-4 rounded-lg hover:bg-slate-800 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {contextoEncabezado ? (
        <TopBar contexto={contextoEncabezado} />
      ) : (
        <TopBar isMainHome mainTitle="Gestión Administrativa Gnóstica" />
      )}
      <div className="flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <button
              type="button"
              onClick={handleCancelarOIrAAdmin}
              className="flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">
                {selectedLumisial ? 'Cancelar' : 'Ir al panel de Administración'}
              </span>
            </button>
          </div>

        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{tituloPantalla}</h2>
          <p className="text-slate-600 text-lg">
            Selecciona un Lumisial para continuar
          </p>
        </div>

        {lastLumisial && lastLumisial.id !== selectedLumisial?.id && (
          <div className="mb-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Star className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900 mb-2">Último Lumisial Usado</p>
                <button
                  onClick={() => handleSelectLumisial(lastLumisial)}
                  className="w-full text-left bg-white hover:bg-amber-50 border border-amber-300 hover:border-amber-400 rounded-lg p-3 transition-all"
                >
                  <h3 className="font-bold text-slate-800 mb-1">{lastLumisial.nombre}</h3>
                  <p className="text-sm text-slate-600">
                    {lastLumisial.diocesis?.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'} {lastLumisial.diocesis?.nombre} - {lastLumisial.provincia_eclesiastica?.nombre}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {lastLumisial.ciudad}{lastLumisial.region && `, ${lastLumisial.region}`}
                  </p>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, ciudad, región, diócesis o provincia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-slate-600 mt-2">
              {filteredLumisiales.length} resultado{filteredLumisiales.length === 1 ? '' : 's'} encontrado{filteredLumisiales.length === 1 ? '' : 's'}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {totalCount === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No se encontraron lumisiales</p>
            </div>
          ) : (
            Object.entries(filteredGrouped).map(([provinciaId, provinciaData]) => (
              <div key={provinciaId} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleProvincia(provinciaId)}
                  className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {expandedProvincias.has(provinciaId) ? (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-600" />
                    )}
                    <span className="font-semibold text-slate-800">
                      {highlightMatch(provinciaData.provinciaNombre, searchTerm)}
                    </span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {Object.values(provinciaData.diocesis).reduce((sum, d) => sum + d.lumisiales.length, 0)} lumisiales
                  </span>
                </button>

                {expandedProvincias.has(provinciaId) && (
                  <div className="bg-white">
                    {Object.entries(provinciaData.diocesis).map(([diocesisId, diocesisData]) => (
                      <div key={diocesisId} className="border-t border-slate-200">
                        <button
                          onClick={() => toggleDiocesis(diocesisId)}
                          className="w-full flex items-center justify-between p-3 pl-8 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            {expandedDiocesis.has(diocesisId) ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <span className="font-medium text-slate-700">
                              {diocesisData.diocesisTipo === 'diocesis' ? 'Diócesis' : 'Prefectura'} {highlightMatch(diocesisData.diocesisNombre, searchTerm)}
                            </span>
                          </div>
                          <span className="text-sm text-slate-500">
                            {diocesisData.lumisiales.length}
                          </span>
                        </button>

                        {expandedDiocesis.has(diocesisId) && (
                          <div className="pl-12 pr-4 pb-2">
                            {diocesisData.lumisiales.map(lumisial => (
                              <LumisialButton
                                key={lumisial.id}
                                lumisial={lumisial}
                                searchTerm={searchTerm}
                                onSelect={handleSelectLumisial}
                                isActive={lumisial.id === selectedLumisial?.id}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      </div>
    </div>
  );
};
